import {useCallback, useEffect, useState} from 'react'
import {useAppDispatch} from 'src/redux/storeHooks'
import {performAdminSessionExpiredLogout} from 'src/utils/adminSessionExpiredLogout'
import {logoutIfImpersonationAdminExpired} from 'src/utils/sessionExpiredLogout'
import {
  ADMIN_SESSION_EXPIRED_EVENT,
  getAdminSessionRemainingMs,
  hasValidAdminSnapshot,
  IMPERSONATION_SESSION_CHANGED_EVENT,
  isImpersonationSessionActive,
} from 'src/utils/impersonationTokens'

const POLL_INTERVAL_MS = 30_000

export function useImpersonationSession() {
  const dispatch = useAppDispatch()
  const [revision, setRevision] = useState(0)

  const refresh = useCallback(() => {
    setRevision((value) => value + 1)
  }, [])

  const handleAdminSessionExpired = useCallback(() => {
    performAdminSessionExpiredLogout(dispatch)
  }, [dispatch])

  useEffect(() => {
    const intervalId = window.setInterval(refresh, POLL_INTERVAL_MS)

    const onExpired = () => {
      handleAdminSessionExpired()
    }

    window.addEventListener(ADMIN_SESSION_EXPIRED_EVENT, onExpired)
    window.addEventListener('pageshow', refresh)
    window.addEventListener('focus', refresh)
    window.addEventListener(IMPERSONATION_SESSION_CHANGED_EVENT, refresh)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener(ADMIN_SESSION_EXPIRED_EVENT, onExpired)
      window.removeEventListener('pageshow', refresh)
      window.removeEventListener('focus', refresh)
      window.removeEventListener(IMPERSONATION_SESSION_CHANGED_EVENT, refresh)
    }
  }, [refresh, handleAdminSessionExpired])

  // Read session state on every render so sidebar/header never go stale.
  const impersonating = isImpersonationSessionActive()
  const canReturn = hasValidAdminSnapshot()

  useEffect(() => {
    if (logoutIfImpersonationAdminExpired(dispatch)) return

    if (!impersonating) return

    if (!hasValidAdminSnapshot()) {
      handleAdminSessionExpired()
      return
    }

    const remaining = getAdminSessionRemainingMs()
    if (remaining !== null && remaining <= 0) {
      handleAdminSessionExpired()
    }
  }, [impersonating, revision, handleAdminSessionExpired, dispatch])

  return {
    isImpersonating: impersonating,
    canReturnToAdmin: canReturn,
    revision,
  }
}
